/**
 * The one line a customer pastes into their page, and the button that gets it
 * onto their clipboard.
 *
 * IT NEVER WRAPS, AND THAT IS THE WHOLE REASON THIS IS A COMPONENT. A `<script>`
 * tag broken across lines by a soft wrap looks copyable and is; what it is not is
 * paste-safe once somebody retypes it from a screenshot, and a support ticket that
 * begins "I copied it exactly" costs more than a horizontal scrollbar. So the code
 * scrolls sideways inside its own box — the same rule the data table follows — and
 * the page body never moves.
 *
 * THE CLIPBOARD MAY SIMPLY NOT BE THERE. `navigator.clipboard` is gated on a
 * secure context, so it is absent over plain HTTP on anything that is not
 * localhost — which is exactly how somebody demoing this from a LAN address will
 * meet it. A copy button that silently does nothing is worse than no copy button,
 * so the unavailable case selects the text and says what to press instead.
 *
 * AND IT CAN BE THERE AND STILL REFUSE, which is a DIFFERENT sentence and not a
 * detail. `writeText` rejects on a document that is not focused and under a
 * restrictive permissions policy, both on perfectly secure origins — the first
 * time this box was driven from an automated browser it rejected for exactly that
 * reason. So the fallback is wired to the catch as well as to the feature test,
 * and the two say different things: blaming a secure-origin problem for an
 * unfocused window sends the reader off to reconfigure a server that was fine.
 *
 * "Copied" REVERTS. It is a confirmation, not a state: leaving it up means the
 * next reader cannot tell whether they pressed it or somebody else did.
 */
import { useEffect, useRef, useState } from 'react'
import { CheckIcon, CopyIcon, TextSelectIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** Long enough to read, short enough that the button is honest again quickly. */
const REVERT_AFTER_MS = 1_500

type Outcome =
  | 'idle'
  | 'copied'
  /** No clipboard API at all — an insecure origin. */
  | 'selected-unavailable'
  /** The API exists and refused. Usually an unfocused document. */
  | 'selected-refused'
  | 'failed'

export function SnippetBox({
  code,
  /** Shown above the box. Omit when the surrounding panel already says it. */
  caption,
  className,
}: {
  code: string
  caption?: string
  className?: string
}) {
  const preRef = useRef<HTMLPreElement | null>(null)
  const [outcome, setOutcome] = useState<Outcome>('idle')

  // One timer, cleared on unmount and on a second press. Without the clear, a
  // reader who presses twice gets the label reverted by the FIRST timer while
  // the second confirmation is still meant to be up.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (timer.current !== null) clearTimeout(timer.current)
    }
  }, [])

  const settle = (next: Outcome) => {
    setOutcome(next)
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = setTimeout(() => setOutcome('idle'), REVERT_AFTER_MS)
  }

  /** Puts the caret round the code so the reader's own copy shortcut works. */
  const selectInstead = (why: 'unavailable' | 'refused') => {
    const el = preRef.current
    const selection = window.getSelection()
    if (!el || !selection) {
      settle('failed')
      return
    }
    const range = document.createRange()
    range.selectNodeContents(el)
    selection.removeAllRanges()
    selection.addRange(range)
    settle(why === 'unavailable' ? 'selected-unavailable' : 'selected-refused')
  }

  // Read at render rather than at click, so the button can be labelled for what
  // it will actually do on this origin.
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard

  const onPress = () => {
    if (!clipboard) {
      selectInstead('unavailable')
      return
    }
    clipboard
      .writeText(code)
      .then(() => settle('copied'))
      // Rejection here is a real answer — an unfocused document, a permissions
      // policy — and it gets its own wording, not the insecure-origin one.
      .catch(() => selectInstead('refused'))
  }

  const selected = outcome === 'selected-unavailable' || outcome === 'selected-refused'

  return (
    <div className={cn('min-w-0', className)}>
      <div className="mb-1.5 flex items-baseline gap-2">
        {caption && <span className="label">{caption}</span>}
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="ml-auto"
          onClick={onPress}
          title={
            clipboard
              ? 'Copies the whole line, including the closing tag.'
              : 'This page is not on a secure origin, so the clipboard API is unavailable. Pressing this selects the code for you to copy.'
          }
        >
          {outcome === 'copied' ? (
            <>
              <CheckIcon /> Copied
            </>
          ) : selected ? (
            <>
              <TextSelectIcon /> Selected
            </>
          ) : clipboard ? (
            <>
              <CopyIcon /> Copy
            </>
          ) : (
            <>
              <TextSelectIcon /> Select
            </>
          )}
        </Button>
      </div>

      {/* `whitespace-pre` and `overflow-x-auto` together: pre-wrap would wrap and
          break the whole point of the box. */}
      <pre
        ref={preRef}
        className="mono overflow-x-auto whitespace-pre rounded-md border bg-muted/60 px-3 py-2 text-xs leading-relaxed"
      >
        {code}
      </pre>

      {outcome === 'selected-unavailable' && (
        <p className="mt-1 text-xs text-muted-foreground">
          Selected rather than copied — this page is not on a secure origin, so the browser
          gives us no clipboard at all. Press Ctrl+C, or Cmd+C on a Mac.
        </p>
      )}
      {outcome === 'selected-refused' && (
        <p className="mt-1 text-xs text-muted-foreground">
          Selected rather than copied — the browser refused the clipboard for this press,
          which usually means the window was not focused. Press Ctrl+C, or Cmd+C on a Mac.
        </p>
      )}
      {outcome === 'failed' && (
        <p className="mt-1 text-xs text-ev-bad">
          Neither copying nor selecting worked in this browser. Select the line by hand.
        </p>
      )}
    </div>
  )
}
